import { useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ApiError, send } from '../lib/api';
import { invalidateAssets } from '../lib/assets';
import { ConfirmDialog } from './ConfirmDialog';
import { useToast } from './Toast';

/**
 * Soft delete with impact check (AST-05): the first attempt refuses when ports are still linked,
 * then the dialog asks to remove the asset together with its links.
 */
export function DeleteAssetDialog({
  asset,
  onClose,
  onDeleted,
}: {
  asset: { id: string; code: string; name: string };
  onClose: () => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [inUse, setInUse] = useState<string | null>(null);

  async function remove() {
    try {
      await send('DELETE', `/api/v1/assets/${asset.id}${inUse ? '?force=1' : ''}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ASSET_IN_USE') {
        setInUse(err.message);
        return;
      }
      throw err;
    }
    await invalidateAssets(qc);
    toast({
      tone: 'success',
      text: `${asset.code} dipindah ke tempat sampah.`,
      action: {
        label: 'Urungkan',
        onClick: () =>
          void send('POST', `/api/v1/assets/${asset.id}/restore`).then(() => invalidateAssets(qc)),
      },
    });
    onDeleted();
  }

  return (
    <ConfirmDialog
      title={inUse ? 'Aset masih terhubung' : `Hapus ${asset.code}?`}
      icon={<Trash2 className="i" aria-hidden />}
      confirmLabel={inUse ? 'Hapus Beserta Koneksi' : 'Hapus'}
      onConfirm={remove}
      onClose={onClose}
    >
      {inUse ? (
        <>
          <span>{inUse}</span>
          <span>
            Port di aset lain yang terhubung akan menjadi kosong. Koneksi tidak ikut kembali saat aset
            dipulihkan.
          </span>
        </>
      ) : (
        <span>
          <strong>{asset.name}</strong> dipindah ke tempat sampah dan bisa dipulihkan dalam 30 hari.
        </span>
      )}
    </ConfirmDialog>
  );
}
